import { Label } from "@/components/ui/label";
import { Profile } from "@/hooks/profile/types";
import { BirthdatePicker } from "./BirthdatePicker";
import { Input } from "@/components/ui/input";
import { AvatarUploader } from "./AvatarUploader";
import { Mail, User, Calendar, CreditCard, Phone, MapPin, ShieldPlus, Heart } from "lucide-react";

interface ProfileDetailsProps {
  profile: Profile;
  onBirthdateChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onAvatarChange: (avatarUrl: string) => Promise<void>;
}

export const ProfileDetails = ({ profile, onBirthdateChange, onEmailChange, onAvatarChange }: ProfileDetailsProps) => {
  return (
    <div className="space-y-6">
      {/* Avatar Section */}
      <AvatarUploader profile={profile} onAvatarChange={onAvatarChange} />
      
      {/* Personal Information Card */}
      <div className="space-y-4 p-4 rounded-xl bg-card/50 border border-border/50">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          <User className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-muted-foreground">Información Personal</h4>
        </div>
        
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="firstName" className="text-sm">Nombre</Label>
            <Input
              id="firstName"
              name="firstName"
              defaultValue={profile.first_name || ""}
              className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
              placeholder="Tu nombre"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName" className="text-sm">Apellido</Label>
            <Input
              id="lastName"
              name="lastName"
              defaultValue={profile.last_name || ""}
              className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
              placeholder="Tu apellido"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="birthdate" className="text-sm flex items-center gap-2">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            Fecha de Nacimiento
          </Label>
          <BirthdatePicker
            value={profile.birthdate || ""}
            onChange={onBirthdateChange}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="dni" className="text-sm flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
            DNI / Documento
          </Label>
          <Input
            id="dni"
            name="dni"
            defaultValue={profile.dni || ""}
            className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
            placeholder="Tu número de documento"
            maxLength={15}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              Teléfono
            </Label>
            <Input
              id="phone"
              name="phone"
              defaultValue={profile.phone || ""}
              className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
              placeholder="Tu número de teléfono"
              maxLength={20}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="emergencyPhone" className="text-sm flex items-center gap-2">
              <ShieldPlus className="h-3.5 w-3.5 text-muted-foreground" />
              Tel. Emergencia
            </Label>
            <Input
              id="emergencyPhone"
              name="emergencyPhone"
              defaultValue={profile.emergency_phone || ""}
              className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
              placeholder="Número de emergencia"
              maxLength={20}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="address" className="text-sm flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            Dirección
          </Label>
          <Input
            id="address"
            name="address"
            defaultValue={profile.address || ""}
            className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
            placeholder="Tu dirección"
            maxLength={200}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="healthInsurance" className="text-sm flex items-center gap-2">
            <Heart className="h-3.5 w-3.5 text-muted-foreground" />
            Obra Social
          </Label>
          <Input
            id="healthInsurance"
            name="healthInsurance"
            defaultValue={profile.health_insurance || ""}
            className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
            placeholder="Tu obra social"
            maxLength={100}
          />
        </div>
      </div>
      
      {/* Account Information Card */}
      <div className="space-y-4 p-4 rounded-xl bg-card/50 border border-border/50">
        <div className="flex items-center gap-2 pb-2 border-b border-border/50">
          <Mail className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium text-muted-foreground">Cuenta</h4>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            defaultValue={profile.username || ""}
            onChange={(e) => onEmailChange(e.target.value)}
            className="bg-background/50 text-foreground border-input/50 focus:border-primary focus:bg-background transition-all"
            placeholder="Tu dirección de email"
          />
        </div>
      </div>
    </div>
  );
};
